import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';

import Typography from '@material-ui/core/Typography';

import CompositionPieGraph from 'components/Graph/CompositionPieGraph';
import FormCheckboxGroup from 'components/FormCheckboxGroup/FormCheckboxGroup';
import withoutEnterSubmit from 'components/withoutEnterSubmit';

import {
  ExternalProductData,
  ExtProductCategory,
  FinancialInstitution,
} from 'pages/Accounts/Edit/components/ExternalProducts/model';
import { CompositionData } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import useOverviewStyles from 'pages/Accounts/Detail/Overview/overviewStyles';
import { EdwardColors } from 'enums';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core';
import createStyles from '@material-ui/core/styles/createStyles';
import { grayscale } from 'helpers/chart';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: (props: { extProdData: ExternalProductData[]; original?: boolean }) => {
      const prods: { [key: string]: { color: string } } = {};
      props.extProdData.forEach(
        (p) =>
          (prods[`& .compositionFilter_${p.id} svg`] = {
            color:
              p.category === ExtProductCategory.LOAN
                ? props.original
                  ? grayscale(EdwardColors.GOLD)
                  : EdwardColors.GOLD
                : props.original
                ? grayscale(p.financialInstitution?.colour || EdwardColors.GRAY_LIGHT)
                : p.financialInstitution?.colour || EdwardColors.GRAY_LIGHT,
          }),
      );
      return {
        ...prods,
        '& > div': { margin: '0 auto' },
        marginTop: theme.spacing(2),
      };
    },
  }),
);

interface Props {
  extProdData: ExternalProductData[];
  composition: CompositionData;
  overview?: boolean;
  original?: boolean;
  finInst?: { [id: string]: FinancialInstitution };
  pdf?: boolean;
  toggleExtProduct?: (id: string, active: boolean) => void;
}

const Composition: React.FC<Props> = ({
  extProdData,
  composition,
  overview,
  original,
  finInst,
  pdf,
  toggleExtProduct,
}) => {
  const { t } = useTranslation();
  const overviewClasses = useOverviewStyles();
  const prodClasses = useStyles({ extProdData, original });

  const initialFilter = extProdData.filter((p) => p.includedInChart).map((p) => p.id);
  const options = extProdData.filter((p) => p.category !== ExtProductCategory.LOAN).map((p) => p.id);
  const labels = extProdData
    .filter((p) => p.category !== ExtProductCategory.LOAN)
    .map(
      (p) =>
        (p.financialInstitution?.title || t('externalProducts_title_property')) +
        ' (' +
        (p.title.length > 10 ? p.title.substr(0, 10) + '…' : p.title) +
        ')',
    );

  return (
    <>
      {overview && (
        <Typography
          variant="h3"
          className={`${
            original ? overviewClasses.originalSubtitle : overview ? overviewClasses.proposedSubtitle : ''
          }`}
        >
          {t('externalProducts_composition')}
        </Typography>
      )}

      <div style={{ display: 'flex', margin: '0 -8px', flexWrap: 'wrap' }}>
        <CompositionPieGraph
          pieDataset={composition.financialInstitution}
          seriesName={'finInst'}
          title={t('backOffice_finInstitutions')}
          //type: 'asset' | 'region' | 'currency' | 'finInst',
          type={'finInst'}
          finInst={finInst}
          pdf={pdf}
          original={original}
        />
        <CompositionPieGraph
          pieDataset={composition.asset}
          seriesName={'asset'}
          type={'asset'}
          title={t('externalProduct_composition_assets')}
          pdf={pdf}
          original={original}
        />
        <CompositionPieGraph
          pieDataset={composition.region}
          seriesName={'region'}
          type={'region'}
          title={t('externalProduct_composition_regions')}
          pdf={pdf}
          original={original}
        />
        <CompositionPieGraph
          pieDataset={composition.currency}
          seriesName={'currency'}
          type={'currency'}
          title={t('externalProduct_composition_currencies')}
          pdf={pdf}
          original={original}
        />
      </div>

      {!pdf && (
        <Formik initialValues={{ compositionFilter: initialFilter }} enableReinitialize onSubmit={() => {}}>
          {(formikProps) => (
            <FormWithoutEnterSubmit>
              <FormCheckboxGroup
                name={'compositionFilter'}
                values={formikProps.values.compositionFilter}
                options={options}
                labels={labels}
                fast={false}
                label={t('externalProducts_composition_filter')}
                afterChangeItem={toggleExtProduct}
                horizontal
                literal
                className={prodClasses.root}
                disabled={overview}
              />
            </FormWithoutEnterSubmit>
          )}
        </Formik>
      )}
    </>
  );
};

export default Composition;
